import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    EvidenceDocumentModel,
    EvidenceDocumentModelListRequestModel,
    EvidenceDocumentModelListResponseModel,
    EvidenceDocumentService,
    EvidenceStatus,
    FilterModel,
} from 'lemunzo-api';
import { FilterMetadata, MessageService, SelectItem } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import {
    catchError,
    EMPTY,
    finalize,
    map,
    Observable,
    of,
    Subject,
    switchMap,
} from 'rxjs';

import { ErrorZone } from '../../enums/error-zone';
import { EvidenceTablePreviewMode } from '../../enums/evidence-table-preview-mode';
import { PrimeNGTagSeverity } from '../../enums/primeng-tag-severity';
import { PrimeNGToastSeverity } from '../../enums/primeng-toast-severity';
import { MediaViewerDialogModel } from '../../models/media-viewer-dialog-model';
import { PrimeNGTableColModel } from '../../models/primeng-table-col-model';
import { ConstantsService } from '../../services/constants.service';
import { DownloadFileService } from '../../services/download-file.service';
import { ErrorService } from '../../services/error.service';
import { EvidenceDocumentPreviewService } from '../../services/evidence-document-preview.service';
import { PrimengLocaleService } from '../../services/primeng-locale.service';
import { QueryService } from '../../services/query.service';
import { StatusService } from '../../services/status-service';
import { TableConfigService } from '../../services/table-config.service';
import { base64ToBlob } from '../../utils/base64-to-blob';
import { isMediaViewerPreviewableContentType } from '../../utils/is-media-viewer-previewable-content-type';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { MediaViewerDialogComponent } from '../media-viewer-dialog/media-viewer-dialog.component';
import { PrimeNGNoRecordsComponent } from '../primeng-no-records/primeng-no-records.component';
import { TooltipCustomComponent } from '../tooltip-custom/tooltip-custom.component';

@Component({
    selector: 'apcam-evidence-document-table',
    templateUrl: './evidence-document-table.component.html',
    styleUrl: './evidence-document-table.component.scss',
    imports: [
        AsyncPipe,
        FormsModule,
        LoadingSpinnerComponent,
        MatButtonModule,
        MatIconModule,
        PrimeNGNoRecordsComponent,
        SelectModule,
        TableModule,
        TagModule,
        TooltipCustomComponent,
        TranslatePipe,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvidenceDocumentTableComponent implements OnInit {
    readonly previewUnavailableLabelKey = input(
        'shared.evidence.table.previewUnavailable'
    );

    readonly loadDocuments =
        input.required<
            (
                request: EvidenceDocumentModelListRequestModel
            ) => Observable<EvidenceDocumentModelListResponseModel>
        >();

    readonly getAdditionalFilters = input<() => FilterModel[]>(() => []);

    readonly showClientProfileId = input(false);

    readonly compactActions = input(false);

    /**
     * Reload trigger driven by the parent after a successful upload.
     * Each changed value reloads the last table request while preserving
     * the current paging, sorting, and filters.
     *
     * The initial value `0` is ignored because PrimeNG performs the first
     * load through `onLazyLoad`.
     */
    readonly refreshToken = input(0);

    readonly errorZone = input.required<ErrorZone>();

    readonly previewMode = input<EvidenceTablePreviewMode>(
        EvidenceTablePreviewMode.DIALOG
    );

    readonly previewRequested = output<EvidenceDocumentModel>();

    /** Emits whenever the loaded page changes (initial load, paging, reload), including the exact request used — lets a caller replay it with a different `paging.skip` to fetch a neighboring page. */
    readonly pageLoaded = output<{
        items: EvidenceDocumentModel[];
        totalCount: number;
        request: EvidenceDocumentModelListRequestModel;
    }>();

    private readonly destroyRef = inject(DestroyRef);
    private readonly dialog = inject(MatDialog);
    private readonly downloadService = inject(DownloadFileService);
    private readonly errorService = inject(ErrorService);
    private readonly evidenceDocumentPreviewService = inject(
        EvidenceDocumentPreviewService
    );
    private readonly evidenceDocumentService = inject(EvidenceDocumentService);
    private readonly primengLocaleService = inject(PrimengLocaleService);
    private readonly statusService = inject(StatusService);
    private readonly tableConfigService = inject(TableConfigService);
    private readonly toastService = inject(MessageService);
    private readonly translateService = inject(TranslateService);

    private readonly loadRequest$ =
        new Subject<EvidenceDocumentModelListRequestModel>();

    /** Last lazy-load query; replayed on refreshToken so post-upload reload keeps page/sort/filters. */
    private listRequestMemory?: EvidenceDocumentModelListRequestModel;

    protected readonly documents = signal<EvidenceDocumentModel[]>([]);
    protected readonly loading = signal(false);
    protected readonly totalCount = signal(0);

    protected readonly filters: Record<string, FilterMetadata> = {};

    /**
     * IDs of documents with a preview request in flight. Same per-row purpose as
     * {@link runningDownloads}: drives the row's loading state and prevents
     * duplicate clicks while the preview is being fetched.
     */
    protected readonly runningPreviews = signal<Set<number>>(new Set());

    /**
     * IDs of documents with a download request in flight. Keyed per document so
     * each row can show its own spinner and disable its own button independently,
     * without blocking downloads/previews on other rows.
     */
    protected readonly runningDownloads = signal<Set<number>>(new Set());

    protected readonly equalsMatchModeOptions = signal<SelectItem[]>([]);
    protected readonly tableCurrentPageReportTemplate = signal('');

    protected readonly tableRowDefault = TableConfigService.TableRowDefault;
    protected readonly tableRowsOptions = TableConfigService.TableRowsOptions;

    protected readonly evidenceStatuses = this.statusService.evidenceStatuses;

    protected readonly locale$: Observable<string> =
        this.primengLocaleService.getLocale() ?? of('');

    protected readonly columns = computed<PrimeNGTableColModel[]>(() => {
        const columns: PrimeNGTableColModel[] = [
            {
                field: 'id',
                label: 'shared.evidence.table.columns.id',
                sort: true,
            },
            {
                field: 'fileName',
                label: 'shared.evidence.table.columns.fileName',
                sort: true,
            },
            {
                field: 'status',
                label: 'shared.evidence.table.columns.status',
                sort: true,
            },
        ];

        if (this.showClientProfileId()) {
            columns.push({
                field: 'clientProfileId',
                label: 'shared.evidence.table.columns.clientProfileId',
                sort: true,
            });
        }

        columns.push({
            field: 'actions',
            label: 'shared.evidence.table.columns.actions',
        });

        return columns;
    });

    constructor() {
        this.loadRequest$
            .pipe(
                switchMap((request) => {
                    this.loading.set(true);

                    return this.loadDocuments()(request).pipe(
                        map((response) => ({ request, response })),
                        catchError((error) => {
                            this.documents.set([]);
                            this.totalCount.set(0);
                            this.errorService.generalToastIgnoringGloballyHandledErrors(
                                this.errorZone(),
                                error
                            );

                            return EMPTY;
                        }),
                        finalize(() => this.loading.set(false))
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(({ request, response }) => {
                const items = response.items ?? [];
                const totalCount = response.totalCount ?? 0;

                this.documents.set(items);
                this.totalCount.set(totalCount);
                this.pageLoaded.emit({ items, totalCount, request });
            });

        effect(() => {
            const refreshToken = this.refreshToken();

            if (refreshToken === 0 || this.listRequestMemory == null) {
                return;
            }

            this.loadData();
        });
    }

    ngOnInit(): void {
        this.setTableConfig();

        this.translateService.onLangChange
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.setTableConfig();
                },
                error: () => {
                    this.errorService.generalToast(this.errorZone());
                },
            });
    }

    protected loadItems(event: TableLazyLoadEvent): void {
        const getAdditionalFilters = this.getAdditionalFilters();
        const additionalFilters = getAdditionalFilters();

        this.listRequestMemory = QueryService.getTableListRequestModel(
            event,
            additionalFilters
        ) as EvidenceDocumentModelListRequestModel;

        this.loadData();
    }

    protected isPreviewable(document: EvidenceDocumentModel): boolean {
        return (
            document.blobDocument?.documentType != null &&
            isMediaViewerPreviewableContentType(
                document.blobDocument.documentType
            )
        );
    }

    /**
     * Evidence has no preview URL endpoint. The file is fetched as base64,
     * converted to a Blob, exposed through a temporary object URL, and displayed
     * in the media viewer dialog. The URL is revoked when the dialog closes.
     */
    protected onPreview(document: EvidenceDocumentModel): void {
        if (this.previewMode() === EvidenceTablePreviewMode.EMIT) {
            this.previewRequested.emit(document);
            return;
        }

        this.addRunningPreview(document.id);

        this.evidenceDocumentPreviewService
            .load(document.id)
            .pipe(
                finalize(() => this.removeRunningPreview(document.id)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (preview) => {
                    if (preview == null) {
                        this.errorService.generalToast(this.errorZone());
                        return;
                    }

                    const { contentType, url: blobUrl } = preview;

                    const dialogRef = this.dialog.open(
                        MediaViewerDialogComponent,
                        {
                            height: '100vh',
                            maxWidth: '',
                            position: { right: '0px' },
                            data: {
                                contentType,
                                fileName: document.fileName,
                                url: blobUrl,
                                hidePrint: false,
                            } satisfies MediaViewerDialogModel,
                            panelClass: [
                                'w-full!',
                                'max-w-full!',
                                'md:max-w-[80vw]!',
                                'lg:max-w-[60vw]!',
                                'xl:max-w-[50vw]!',
                            ],
                        }
                    );

                    dialogRef
                        .afterClosed()
                        .pipe(
                            finalize(() => URL.revokeObjectURL(blobUrl)),
                            takeUntilDestroyed(this.destroyRef)
                        )
                        .subscribe();
                },
                error: () => {
                    this.errorService.generalToast(this.errorZone());
                },
            });
    }

    protected onDownload(document: EvidenceDocumentModel): void {
        this.addRunningDownload(document.id);

        this.toastService.add({
            key: ConstantsService.TOAST_KEY,
            severity: PrimeNGToastSeverity.INFO,
            summary: this.translateService.instant(
                'shared.toast.download.summary',
                {
                    value: document.fileName,
                }
            ),
        });

        this.evidenceDocumentService
            .getEvidenceDocument(document.id)
            .pipe(
                finalize(() => this.removeRunningDownload(document.id)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (file) => {
                    this.downloadService.downloadBlobFile(
                        base64ToBlob(file.data, file.contentType),
                        document.fileName
                    );
                },
                error: () => {
                    this.errorService.generalToast(this.errorZone());
                },
            });
    }

    protected getEvidenceStatusLabel(status: EvidenceStatus): string {
        return this.statusService.evidenceStatusValueToLabel(status);
    }

    protected getEvidenceStatusSeverity(
        status: EvidenceStatus
    ): PrimeNGTagSeverity {
        const evidenceStatus = this.evidenceStatuses.find(
            (option) => option.value === status
        );

        return evidenceStatus?.primengTagSeverity ?? PrimeNGTagSeverity.INFO;
    }

    private loadData(): void {
        if (this.listRequestMemory == null) {
            return;
        }

        this.loadRequest$.next(this.listRequestMemory);
    }

    private setTableConfig(): void {
        this.tableCurrentPageReportTemplate.set(
            this.tableConfigService.getTableCurrentPageReportTemplate()
        );

        this.equalsMatchModeOptions.set(
            this.tableConfigService.getEqualsMatchModeOptions()
        );
    }

    private addRunningPreview(documentId: number): void {
        this.runningPreviews.update((previous) =>
            new Set(previous).add(documentId)
        );
    }

    private removeRunningPreview(documentId: number): void {
        this.runningPreviews.update((previous) => {
            const next = new Set(previous);
            next.delete(documentId);
            return next;
        });
    }

    private addRunningDownload(documentId: number): void {
        this.runningDownloads.update((previous) =>
            new Set(previous).add(documentId)
        );
    }

    private removeRunningDownload(documentId: number): void {
        this.runningDownloads.update((previous) => {
            const next = new Set(previous);
            next.delete(documentId);
            return next;
        });
    }
}
